import { until } from '@vueuse/core';

// The lists that put the reader back where they were, the sessionStorage key of that position, and the pages their
// cards open.
const LIST_SCROLL_POSITIONS: Record<string, {key: string, cards: string[]}> = {
  'events-eventId-agenda': {key: 'scrollTop-multi-day-agenda', cards: ['events-eventId-session-id']},
  'events-eventId-my-event': {key: 'scrollTop-multi-day-my-event', cards: ['events-eventId-session-id']},
  'events-eventId-speakers': {key: 'scrollTop-speakers', cards: ['events-eventId-speaker-id']},
  'events-eventId-partners': {key: 'scrollTop-partners', cards: ['events-eventId-partner-id']},
  'events-eventId-goodies': {key: 'scrollTop-goodies', cards: ['events-eventId-goodie-id']},
};

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (import.meta.server) {
    return;
  }

  //region Last event route restoration

  if (typeof to.name === 'string' && to.name.startsWith('events-eventId')) {
    localStorage.setItem(LAST_EVENT_ROUTE_ID, to.params.eventId as string);
  }

  if (to.name === 'index') {
    if (!from.name) {
      const lastEventId = localStorage.getItem(LAST_EVENT_ROUTE_ID);
      if (lastEventId) {
        const { status } = useAuth();
        // Wait for sidebase-auth to finish resolving the session before redirecting.
        if (status.value === 'loading') {
          await until(status).not.toBe('loading');
        }
        if (status.value === 'authenticated') {
          return navigateTo({ name: 'events-eventId', params: { eventId: lastEventId } });
        }
      }
    } else if (typeof from.name === 'string' && from.name.startsWith('events-eventId')) {
      // User navigated back to events list from within an event.
      localStorage.removeItem(LAST_EVENT_ROUTE_ID);
    }
  }

  //endregion

  //region Sections that are not open yet

  // Attendees, partners, goodies and booth activities open with the agenda (TAL-11054). A user who types or follows a
  // link to one of them lands on the "not available" page instead. The event itself carries the switch, so on a cold
  // load it is fetched here, before the page mounts: a detail page asks the API for its data as soon as it starts, and
  // the 403 would otherwise replace the whole app with the error page. app.vue covers the switch being turned on while
  // one of these pages is already open.
  if (typeof to.name === 'string' && COMMUNITY_ROUTE_NAMES.includes(to.name)) {
    const eventsStore = useEventsStore();
    const eventId = Number(to.params.eventId);
    if (eventsStore.eventData?.id !== eventId) {
      try {
        await eventsStore.loadEventPayload(eventId);
      } catch {
        // Offline or failing: the page handles that the way it always did.
      }
    }
    if (eventsStore.eventData?.id === eventId && !eventsStore.communityAvailable) {
      return navigateTo({name: 'events-eventId-not-available', params: {eventId: to.params.eventId}}, {replace: true});
    }
  }

  //endregion

  //region YouTube player handling

  const youtubeStore = useYouTubePlayerStore();
  const {videoCurrentRoute, videoPlayerRef} = storeToRefs(youtubeStore);

  if(videoPlayerRef.value && typeof videoPlayerRef.value.getCurrentTime === 'function') {
    youtubeStore.updateVideoTimeAndStatus();
    videoPlayerRef.value.destroy()
    videoPlayerRef.value = null;
  }
  videoCurrentRoute.value = to.name;
  if(['index', 'user-link-ticket', 'user-login', 'user-signup'].includes(to.name as string)) {
    youtubeStore.setCurrentVideo(null, null);
  }

  //endregion

  //region Notifications handling

  if(from.name === 'notifications') {
    const notificationsStore = useNotificationsStore();
    const {hasUnreadNotifications} = storeToRefs(notificationsStore);
    if(hasUnreadNotifications.value) {
      notificationsStore.loadNotifications(true);
    }
  }

  //endregion

  //region Scroll position reset

  // Each list saves its position when one of its cards is tapped. Left another way (the menu), that position is out
  // of date.
  const leavingList = LIST_SCROLL_POSITIONS[String(from.name)];
  if(leavingList && to.path !== from.path && !leavingList.cards.includes(String(to.name))) {
    sessionStorage.removeItem(leavingList.key);
  }
  // Opened anew (the menu, a link), a list starts at the top. Come back to through the history (the header's back
  // arrow, iOS swipe back, Android back), from however many pages deep, it keeps the position its card saved. The
  // browser has already moved to the list's entry when this runs; on a push the entry is still the page being left.
  const arrivingList = LIST_SCROLL_POSITIONS[String(to.name)];
  const comingBack = window.history.state?.current === to.fullPath;
  if(arrivingList && to.path !== from.path && !comingBack) {
    sessionStorage.removeItem(arrivingList.key);
  }
  // A page that restores itself starts from the top, and must get there before it mounts: the pages swap without
  // waiting for a frame (app.vue), so a scroll to the top from the router would land after the page's own restore.
  if(to.path !== from.path && SELF_RESTORING_PAGES.has(String(to.name))) {
    window.scrollTo({top: 0, behavior: 'instant'});
  }

  //endregion

})
